import React, { useState, useEffect } from 'react';
import { Plus, FolderOpen, X, Edit2 } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';


/**
 * ProposalSelector Component
 * Allows user to create a new proposal or select an existing one
 * to add calculations to
 */
export default function ProposalSelector({ selectedProposalId, onProposalChange, className = '' }) {
  const [proposals, setProposals] = useState([]);
  const [showNewProposalModal, setShowNewProposalModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newProposalName, setNewProposalName] = useState('');
  const [editProposalName, setEditProposalName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await api.get('/collections');
      setProposals(response.data.collections || []);
    } catch (error) {
      console.error('Error fetching proposals:', error);
    }
  };

  const handleCreateProposal = async () => {
    if (!newProposalName.trim()) {
      toast.error('Please enter a proposal name');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(
        '/collections',
        { name: newProposalName }
      );

      const newProposal = response.data.collection;
      setProposals([...proposals, newProposal]);
      onProposalChange(newProposal.id);
      setNewProposalName('');
      setShowNewProposalModal(false);
      toast.success(`Proposal "${newProposalName}" created`);
    } catch (error) {
      console.error('Error creating proposal:', error);
      toast.error('Failed to create proposal');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProposal = async () => {
    if (!editProposalName.trim()) {
      toast.error('Please enter a proposal name');
      return;
    }

    try {
      setLoading(true);
      await api.put(
        `/collections/${selectedProposalId}`,
        { name: editProposalName }
      );

      // Update proposals list
      setProposals(proposals.map(p => 
        p.id === selectedProposalId ? { ...p, name: editProposalName } : p
      ));
      
      setShowEditModal(false);
      toast.success('Proposal name updated');
    } catch (error) {
      console.error('Error updating proposal:', error);
      toast.error('Failed to update proposal name');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = () => {
    const currentProposal = proposals.find(p => p.id === selectedProposalId);
    if (currentProposal) {
      setEditProposalName(currentProposal.name || '');
      setShowEditModal(true);
    }
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        <FolderOpen className="w-4 h-4 inline mr-2" />
        Proposal Collection
      </label>

      <div className="flex gap-2">
        <select
          value={selectedProposalId || ''}
          onChange={(e) => onProposalChange(e.target.value || null)}
          className="input-field flex-1"
        >
          <option value="">Select a proposal...</option>
          {proposals.map((proposal) => (
            <option key={proposal.id} value={proposal.id}>
              {proposal.name} ({proposal.scenario_count || 0} services)
            </option>
          ))}
        </select>

        {selectedProposalId && (
          <button
            onClick={openEditModal}
            className="btn-secondary flex items-center gap-2 whitespace-nowrap"
            title="Edit proposal name"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        )}

        <button
          onClick={() => setShowNewProposalModal(true)}
          className="btn-secondary flex items-center gap-2 whitespace-nowrap"
          title="Create new proposal"
        >
          <Plus className="w-4 h-4" />
          New
        </button>
      </div>

      {/* New Proposal Modal */}
      {showNewProposalModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowNewProposalModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Create New Proposal</h3>
              <button
                onClick={() => setShowNewProposalModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Proposal Name
              </label>
              <input
                type="text"
                value={newProposalName}
                onChange={(e) => setNewProposalName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateProposal()}
                placeholder="e.g., Los Angeles County 2024 Q1"
                className="input-field w-full"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowNewProposalModal(false)}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProposal}
                className="btn-primary"
                disabled={loading || !newProposalName.trim()}
              >
                {loading ? 'Creating...' : 'Create Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Proposal Modal */}
      {showEditModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowEditModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Edit Proposal Name</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Proposal Name
              </label>
              <input
                type="text"
                value={editProposalName || ''}
                onChange={(e) => setEditProposalName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleEditProposal()}
                className="input-field w-full"
                placeholder="Enter proposal name..."
                autoFocus
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleEditProposal}
                className="btn-primary"
                disabled={loading || !(editProposalName || '').trim()}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
